
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface PendingProposal {
  id: number;
  sender_id: string;
  item_id: number | null;
  vehicle_id: number | null;
  offered_item_id: number | null;
  offered_vehicle_id: number | null;
  message: string | null;
  status: string;
  created_at: string;
  sender?: {
    username: string;
    avatar_url: string | null;
  };
}

export function usePendingProposals(userId: string | null) {
  const [pendingProposals, setPendingProposals] = useState<PendingProposal[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setPendingProposals([]);
      setPendingCount(0);
      setLoading(false);
      return;
    }

    const fetchPendingProposals = async () => {
      try {
        const { data, error } = await supabase
          .from('swap_proposals')
          .select(`
            id,
            sender_id,
            item_id,
            vehicle_id,
            offered_item_id,
            offered_vehicle_id,
            message,
            status,
            created_at
          `)
          .eq('receiver_id', userId)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching pending proposals:', error);
          return;
        }

        setPendingProposals(data || []);
        setPendingCount(data?.length || 0);
      } catch (err) {
        console.error('Error in fetchPendingProposals:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingProposals();

    // Subscribe to real-time changes for swap proposals
    const channel = supabase
      .channel('pending-proposals-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'swap_proposals',
          filter: `receiver_id=eq.${userId}`,
        },
        () => {
          // Refetch when any change occurs
          fetchPendingProposals();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  return { pendingProposals, pendingCount, loading };
}
